import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于喵绅士',
    description: '喵绅士是一个专为爱猫人士打造的高雅精品平台，汇聚猫咪文化、绅士美学与精致生活方式。',
    url: 'https://yufangwlkk.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '喵绅士',
      url: 'https://yufangwlkk.cn',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '喵绅士',
    alternateName: 'Mr. Meow',
    url: 'https://yufangwlkk.cn',
    description: '高雅猫咪文化与精品生活方式平台',
    foundingDate: '2023',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '愚园路168号',
      addressLocality: '上海',
      addressRegion: '静安区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-21-5208-6666',
      contactType: 'customer service',
      email: 'hello@yufangwlkk.cn',
    },
    sameAs: [
      'https://weibo.com/mrmeow',
      'https://www.xiaohongshu.com/mrmeow',
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('company_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    try {
      const fd = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9slhnh4ne17ei0sdjbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const milestones = [
    { year: '2023', title: '品牌创立', desc: '三位爱猫绅士在上海愚园路创立喵绅士，立志打造中国最具品味的猫咪文化平台' },
    { year: '2024', title: '内容爆发', desc: '累计发布原创精品文章超过200篇，全网粉丝突破50万，成为猫咪生活方式领域的标杆媒体' },
    { year: '2025', title: '全国布局', desc: '在北京、深圳、成都设立城市编辑部，举办线下喵绅士沙龙活动超过30场，影响力持续扩大' },
  ];

  const testimonials = [
    { name: '李女士', role: '资深铲屎官', text: '自从关注了喵绅士，我的养猫生活彻底改变了。不只是学会了科学喂养，更重要的是找到了与猫咪相处的优雅方式。' },
    { name: '张先生', role: '室内设计师', text: '喵绅士的"极简主义猫宅"系列文章给了我巨大的设计灵感。现在我为客户设计猫友好空间时，都会推荐他们先看喵绅士。' },
    { name: '小野老师', role: '日料主厨', text: '猫与茶道那篇文章让我重新审视了生活中的仪式感。喵绅士不只是一个猫咪平台，更是一种生活哲学的传播者。' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <main className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-950 mb-3">
              关于喵绅士
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              高雅猫咪文化与精品生活方式 —— 我们不只是谈论猫，我们谈论一种优雅的生活态度
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <p className="text-xs tracking-widest uppercase text-primary-500 font-medium mb-3 whitespace-nowrap">Brand Story</p>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    喵绅士诞生于2023年的上海。三位热爱猫咪的绅士——一位独立出版人、一位室内设计师和一位日料主厨——在愚园路的一间老洋房里，决定共同创建一个前所未有的猫咪文化平台。
                  </p>
                  <p>
                    他们深信：养猫不只是一种爱好，更是一种生活哲学。猫咪身上的优雅、从容、独立与好奇心，恰恰是现代绅士最珍视的品质。喵绅士的使命，就是将这些品质传递给每一位爱猫人士。
                  </p>
                  <p>
                    从最初的三人小团队，到如今遍布全国的编辑网络，喵绅士始终坚持一个信念：<strong>让每一位爱猫人都能活得像一位真正的喵绅士</strong>。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=Three%20elegant%20gentlemen%20with%20cats%20in%20vintage%20Shanghai%20apartment%2C%20warm%20afternoon%20light%2C%20old%20french%20concession%20style%2C%20refined%20lifestyle%2C%20editorial%20photography&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="喵绅士品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-xs tracking-widest uppercase text-accent-500 font-medium mb-3 whitespace-nowrap">Milestones</p>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
                发展历程
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {milestones.map((m) => (
                <div key={m.year} className="text-center p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60">
                  <span className="inline-block text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-3">
                    {m.year}
                  </span>
                  <h3 className="text-lg font-semibold text-foreground-900 mb-2 whitespace-nowrap">{m.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-xs tracking-widest uppercase text-secondary-500 font-medium mb-3 whitespace-nowrap">Testimonials</p>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
                喵友口碑
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-sm w-4 h-4 flex items-center justify-center" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5 italic">
                    "{t.text}"
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{t.name}</p>
                    <p className="text-xs text-foreground-500 whitespace-nowrap">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <p className="text-xs tracking-widest uppercase text-primary-500 font-medium mb-3 whitespace-nowrap">Contact</p>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-5">
                  联系我们
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">地址</p>
                      <p className="text-sm text-foreground-600">上海市静安区愚园路168号 喵绅士编辑部</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">邮箱</p>
                      <p className="text-sm text-foreground-600">hello@yufangwlkk.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500 w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-0.5">电话</p>
                      <p className="text-sm text-foreground-600">021-5208-6666</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="p-8 rounded-lg bg-background-50 border border-background-200/60 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-50 flex items-center justify-center">
                      <i className="ri-check-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center" />
                    </div>
                    <p className="text-foreground-900 font-semibold mb-1">消息已发送！</p>
                    <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内回复</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/60 space-y-4"
                  >
                    <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2 whitespace-nowrap">发送消息</h3>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（不超过500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}